import { MessageAppend, ToolInvocation } from '@bitte-ai/types';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { BitteSpinner } from '../../BitteSpinner';
import { CodeBlock } from '../../CodeBlock';
import { ToolCallResult } from './ToolCallResult';

const isTransactionTool = (toolName: string): boolean => {
  const transactionKeywords = [
    'send',
    'transfer',
    'swap',
    'approve',
    'stake',
    'unstake',
    'bridge',
    'deposit',
    'withdraw',
    'buy',
    'sell',
    'trade',
    'mint',
    'burn',
    'claim',
    'delegate',
    'undelegate',
    'vote',
    'execute',
    'transaction',
    'tx',
    'generate-evm-tx',
    'generate_evm_tx',
    'generate_transaction',
    'intents',
    'cowswap',
  ];

  return transactionKeywords.some((keyword) =>
    toolName.toLowerCase().includes(keyword),
  );
};

interface ToolCallProps {
  toolInvocation: ToolInvocation;
  hideToolCall?: boolean;
  showBorder?: boolean;
  messageId: string;
  groupKey: string;
  chatId: string;
  creator?: string;
  borderColor?: string;
  messageBackgroundColor?: string;
  textColor?: string;
  customApproveTxButton?: React.ComponentType<any>;
  customDeclineTxButton?: React.ComponentType<any>;
  customTxContainer?: React.ComponentType<any>;
  append?: MessageAppend;
  customToolComponents?: any[];
}

const ToolCallComponent = ({
  toolInvocation,
  hideToolCall,
  showBorder,
  messageId,
  groupKey,
  chatId,
  creator,
  borderColor,
  messageBackgroundColor,
  textColor,
  customApproveTxButton,
  customDeclineTxButton,
  customTxContainer,
  append,
  customToolComponents,
}: ToolCallProps) => {
  const { toolName, toolCallId, state, result } = toolInvocation;

  // Enhanced state management for smoother transitions
  const [persistentLoading, setPersistentLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [stableResult, setStableResult] = useState(result);
  const hasResultRef = useRef(false);
  const initialLoadingRef = useRef(true);

  // Update stable result when we get new data
  useEffect(() => {
    if (result && !result.error) {
      setStableResult(result);
      hasResultRef.current = true;
    }
  }, [result]);

  // Manage loading states more intelligently
  useEffect(() => {
    const isLoading = state === 'running' || state === undefined || !result;
    const hasValidResult = result && !result.error;

    if (isLoading && !hasResultRef.current) {
      setPersistentLoading(true);
      setShowContent(false);
      initialLoadingRef.current = true;
    } else if (hasValidResult && initialLoadingRef.current) {
      // First time getting valid result - show with delay to prevent flashing
      setTimeout(() => {
        setPersistentLoading(false);
        setShowContent(true);
        initialLoadingRef.current = false;
      }, 100);
    } else if (hasValidResult && !initialLoadingRef.current) {
      // Already have result, update immediately
      setPersistentLoading(false);
      setShowContent(true);
    }
  }, [state, result]);

  // Memoize the content to prevent unnecessary re-renders
  const content = useMemo(() => {
    // Always show error states immediately
    if (result?.error && !hideToolCall) {
      return <CodeBlock content={`Error: ${result.error}`} />;
    }

    // Show content when we have a stable result and showContent is true
    if (showContent && stableResult && !stableResult.error) {
      return (
        <ToolCallResult
          toolName={toolName}
          result={stableResult}
          hideToolCall={hideToolCall}
          showBorder={showBorder}
          messageId={messageId}
          groupKey={groupKey}
          chatId={chatId}
          creator={creator}
          borderColor={borderColor}
          messageBackgroundColor={messageBackgroundColor}
          textColor={textColor}
          customApproveTxButton={customApproveTxButton}
          customDeclineTxButton={customDeclineTxButton}
          customTxContainer={customTxContainer}
          append={append}
          customToolComponents={customToolComponents}
        />
      );
    }

    // Show loading state when persistent loading is true
    if (persistentLoading) {
      return (
        <div className="bitte-space-y-4">
          <div className="bitte-bg-secondary bitte-rounded-lg bitte-p-4">
            <div className="bitte-flex bitte-items-center bitte-gap-4">
              <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
              <span className="bitte-text-secondary-foreground bitte-font-medium">
                {isTransactionTool(toolName)
                  ? 'We are generating your transaction...'
                  : 'Processing your request...'}
              </span>
            </div>
          </div>
          {isTransactionTool(toolName) && (
            <div className="bitte-flex bitte-justify-center">
              <BitteSpinner width={80} height={80} color="#FFFFFF" />
            </div>
          )}
        </div>
      );
    }

    // Default to null if no content should be shown
    return null;
  }, [
    result,
    hideToolCall,
    showContent,
    stableResult,
    persistentLoading,
    toolName,
    messageId,
    groupKey,
    chatId,
    creator,
    borderColor,
    messageBackgroundColor,
    textColor,
    customApproveTxButton,
    customDeclineTxButton,
    customTxContainer,
    append,
    customToolComponents,
    showBorder,
  ]);

  return (
    <div key={`${toolCallId}`}>
      <div className="bitte-relative">{content}</div>

      {!hideToolCall && (
        <div className="bitte-mt-2 bitte-border-t" style={{ borderColor }} />
      )}
    </div>
  );
};

// Simplified memo comparison
export const ToolCall = memo(ToolCallComponent, (prevProps, nextProps) => {
  // Quick reference check for toolInvocation object
  if (prevProps.toolInvocation === nextProps.toolInvocation) {
    return true;
  }

  // Check if the essential properties have changed
  const prevTool = prevProps.toolInvocation;
  const nextTool = nextProps.toolInvocation;

  if (prevTool.toolCallId !== nextTool.toolCallId) return false;
  if (prevTool.toolName !== nextTool.toolName) return false;
  if (prevTool.state !== nextTool.state) return false;
  if (prevTool.result !== nextTool.result) return false;

  // Check other props that might affect rendering
  if (prevProps.hideToolCall !== nextProps.hideToolCall) return false;
  if (prevProps.showBorder !== nextProps.showBorder) return false;
  if (prevProps.messageId !== nextProps.messageId) return false;
  if (prevProps.groupKey !== nextProps.groupKey) return false;
  if (prevProps.chatId !== nextProps.chatId) return false;

  return true;
});

ToolCall.displayName = 'ToolCall';
